/**
 * Cancel a session (or a whole recurrence group) with a reason.
 */
import * as React from 'react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { Select } from '@/components/forms/Select';
import { Textarea } from '@/components/forms/Textarea';
import { SESSION_CANCEL_REASON_OPTIONS } from '@/components/sessions/session-configs';
import {
  buildCancelSessionConfirmPayload,
  cancelSessionDescription,
  isCancelReasonDetailRequired,
  nextCancelReasonDetail,
} from '@/components/sessions/inperson/session-form-utils';
import { useFormValidation } from '@/hooks/useFormValidation';
import type { CancelSessionReasonCode } from '@/types/inperson/session.types';
import { cancelSessionValidationSchema } from '@/utils/validation-schemas-utils';

/** Props for {@link CancelSessionModal}. */
interface CancelSessionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reasonCode: CancelSessionReasonCode, reasonDetail?: string) => void;
  isLoading?: boolean;
  itemName?: string;
  isGroupCancel?: boolean;
  childCount?: number;
}

/** Cancel a session (or a whole recurrence group) with a reason. */
export const CancelSessionModal: React.FC<CancelSessionModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  itemName,
  isGroupCancel = false,
  childCount,
}) => {
  const [reasonCode, setReasonCode] = React.useState<CancelSessionReasonCode | ''>('');
  const [reasonDetail, setReasonDetail] = React.useState('');
  const validation = useFormValidation(cancelSessionValidationSchema);
  const showDetailField = isCancelReasonDetailRequired(reasonCode);
  const title = isGroupCancel ? 'Annuler toutes les séances du groupe' : 'Annuler la séance';
  const description = cancelSessionDescription(isGroupCancel, childCount, itemName);

  function handleClose(): void {
    setReasonCode('');
    setReasonDetail('');
    validation.resetValidation();
    onClose();
  }

  function handleReasonChange(value: string): void {
    const nextCode = value as CancelSessionReasonCode;
    const nextDetail = nextCancelReasonDetail(value, reasonDetail);
    const next = { reasonCode: nextCode, reasonDetail: nextDetail };
    setReasonCode(nextCode);
    setReasonDetail(nextDetail);
    validation.validateField('reasonCode', nextCode, next);
    validation.markAsTouched('reasonCode');
  }

  function handleReasonDetailChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    const nextDetail = event.target.value;
    const next = { reasonCode, reasonDetail: nextDetail };
    setReasonDetail(nextDetail);
    validation.validateField('reasonDetail', nextDetail, next);
    validation.markAsTouched('reasonDetail');
  }

  function handleConfirm(): void {
    if (!reasonCode || !validation.validateForm({ reasonCode, reasonDetail })) return;
    const payload = buildCancelSessionConfirmPayload(reasonCode, reasonDetail);
    onConfirm(payload.reasonCode, payload.reasonDetail);
  }

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      size="md"
      header={{
        preset: 'danger',
        iconSize: 'md',
        title,
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      footer={
        <div className="flex gap-2 justify-end w-full">
          <Button preset="modal-cancel" onClick={handleClose} disabled={isLoading}>
            Fermer
          </Button>
          <Button
            preset="modal-danger"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isLoading || !validation.isFormValid()}
          >
            Confirmer l&apos;annulation
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        <p className="text-sm text-muted-foreground">{description}</p>

        <Select
          label="Motif d'annulation"
          required
          placeholder="Sélectionner un motif"
          options={SESSION_CANCEL_REASON_OPTIONS}
          value={reasonCode}
          onChange={handleReasonChange}
          state={validation.getFieldState('reasonCode')}
          error={validation.getFieldError('reasonCode')}
        />

        {showDetailField ? (
          <Textarea
            id="cancel-session-reason-detail"
            label="Précisions"
            required
            placeholder="Décrivez la raison de l'annulation..."
            maxLength={1000}
            showCharCount
            value={reasonDetail}
            onChange={handleReasonDetailChange}
            error={validation.getFieldError('reasonDetail')}
          />
        ) : null}
      </div>
    </BaseModal>
  );
};
